"use client";

import { SignInButton, UserButton, useUser } from "@clerk/clerk-react";
import { useUserRole } from "@/hooks/useUserRole";

type ClerkUserButtonProps = {
  onDebugClick?: () => void;
};

export function ClerkUserButton({ onDebugClick }: ClerkUserButtonProps) {
  const { user, isLoaded } = useUser();
  const { loading: roleLoading, userRole, isAdmin } = useUserRole();

  if (!isLoaded || roleLoading) {
    return <div className="h-8 w-8 animate-pulse rounded-full bg-gray-800" />;
  }

  // User is signed in - show user button with admin link if applicable
  if (user) {
    return (
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-400">
          {user.firstName || user.emailAddresses[0].emailAddress}
        </span>
        <UserButton
          appearance={{
            variables: {
              colorBackground: "#0f172a",
              colorText: "#e5e7eb",
              colorTextSecondary: "#9ca3af",
              colorPrimary: "#6366f1",
              colorDanger: "#ef4444",
              colorInputBackground: "#111827",
              colorInputText: "#e5e7eb",
            },
            elements: {
              avatarBox: "h-8 w-8",
              userButtonPopoverRootBox: "bg-gray-900 text-gray-200",
              userButtonPopoverCard:
                "bg-gray-900 border border-gray-800 text-gray-200 shadow-xl",
              userButtonPopoverMain: "bg-gray-900 text-gray-200",
              userButtonPopoverActions: "bg-gray-900 text-gray-200",
              userButtonPopoverActionButton:
                "hover:bg-gray-800 !text-slate-300",
              userButtonPopoverActionButtonIconBox: "!text-slate-300",
              userButtonPopoverActionButtonIcon: "!text-slate-300",
              userButtonPopoverFooter: "bg-gray-900 text-gray-400",
              userButtonPopoverFooterPagesLink:
                "text-gray-400 hover:text-gray-200",
              userButtonPopoverCustomItemButton:
                "hover:bg-gray-800 !text-slate-300 font-medium",
              userButtonPopoverCustomItemButtonIconBox: "text-emerald-400",
            },
          }}
        >
          {(userRole?.role || (isAdmin && onDebugClick)) && (
            <UserButton.MenuItems>
              {userRole?.role &&
                (userRole.role === "ADMIN" ||
                userRole.role === "OWNER" ||
                userRole.role === "ROOT" ? (
                  <UserButton.Link
                    href="/admin"
                    label={`Role · ${userRole.role}`}
                    labelIcon={<span className="text-xs">●</span>}
                  />
                ) : (
                  <UserButton.Action
                    label={`Role · ${userRole.role}`}
                    labelIcon={<span className="text-xs">●</span>}
                    onClick={() => undefined}
                  />
                ))}
              {isAdmin && onDebugClick && (
                <UserButton.Action
                  label="Debug"
                  labelIcon={<span className="text-xs">●</span>}
                  onClick={onDebugClick}
                />
              )}
            </UserButton.MenuItems>
          )}
        </UserButton>
      </div>
    );
  }

  // User is not signed in - show sign-in button
  return (
    <SignInButton mode="modal">
      <button className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors">
        Sign In
      </button>
    </SignInButton>
  );
}
